import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { NgxPaginationModule } from 'ngx-pagination';
import * as XLSX from 'xlsx';

import { AppState, IUniverseSelection } from '../../../../../../state/app.state';
import { loadSidenavData, submitSearch, changeActiveSideTab, setUniverseSelection } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey, selectUniverseSelectionbyTabId } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import {
  IInnovationMetricsCompany, IInnovationMetricsSnapshot
} from '../../../../../../core/models/results.model';
import {
  getBracketColor, getBracketArrow, getBracketTooltip, getBarColor, getSignalColor, getSignalBgColor
} from '../../../../../../shared/utils/bracket.utils';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { REGION_CODES } from 'public/country-region-codes';
import { AddToWatchlistPopupComponent } from 'src/app/shared/components/add-to-watchlist-popup/add-to-watchlist-popup.component';
import { UniverseSelectorComponent } from 'src/app/shared/components/universe-selector/universe-selector.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

export type CompetitorMode = 'public' | 'private' | 'custom';

interface CompetitorRow extends IInnovationMetricsCompany {
  rank: number;
  favorite?: boolean;
  score_bracket?: number;
  rank_bracket?: number;
}

// --- Optional extra columns (citations / green / high quality) ---

interface ExtraColumn {
  key: string;                                 // sort key + localStorage id
  label: string;
  sublabel: string;
  tooltip: string;
  decimals?: string;                           // NullableNumberPipe format, default '1.0-0'
  value: (c: CompetitorRow) => number | null;
  secondary: (c: CompetitorRow) => string;     // "+12% vs prior 12m" | "14% of filings"
}

const ratio = (num: number | null, den: number | null): number | null =>
  num == null || !den ? null : (num / den) * 100;

const shareLabel = (num: number | null, den: number | null): string => {
  const r = ratio(num, den);
  return r == null ? '' : `${Math.round(r)}% of filings`;
};

const perInventionLabel = (citations: number | null, size: number | null): string =>
  citations == null || !size ? '' : `${(citations / size).toFixed(2)} per invention`;

// TODO: replace with the final wording from Aytac.
const PLACEHOLDER_TOOLTIP = 'Placeholder- waiting for aytac.';

export const EXTRA_COLUMNS: ExtraColumn[] = [
  {
    key: 'citations_received_12m', label: 'Citations received', sublabel: '1y',
    tooltip: PLACEHOLDER_TOOLTIP,
    value: c => c.citations_received_12m,
    secondary: c => perInventionLabel(c.citations_received_12m, c.invention_count_120m),
  },
  {
    key: 'green_invention_count_12m', label: 'Green inventions', sublabel: '1y',
    tooltip: PLACEHOLDER_TOOLTIP,
    value: c => c.green_invention_count_12m,
    secondary: c => shareLabel(c.green_invention_count_12m, c.invention_count_12m),
  },
  {
    key: 'green_invention_count_120m', label: 'Green inventions', sublabel: '10y',
    tooltip: PLACEHOLDER_TOOLTIP,
    value: c => c.green_invention_count_120m,
    secondary: c => shareLabel(c.green_invention_count_120m, c.invention_count_120m),
  },
  {
    key: 'high_quality_invention_count_12m', label: 'High-quality inventions', sublabel: '1y',
    tooltip: PLACEHOLDER_TOOLTIP,
    value: c => c.high_quality_invention_count_12m,
    secondary: c => shareLabel(c.high_quality_invention_count_12m, c.invention_count_12m),
  },
  {
    key: 'high_quality_invention_count_120m', label: 'High-quality inventions', sublabel: '10y',
    tooltip: PLACEHOLDER_TOOLTIP,
    value: c => c.high_quality_invention_count_120m,
    secondary: c => shareLabel(c.high_quality_invention_count_120m, c.invention_count_120m),
  },
];

const EXTRA_COLUMNS_STORAGE_KEY = 'sia_extra_columns';

@Component({
  selector: 'app-competitors-table-v2',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule,
    MatTooltipModule, MatSlideToggleModule,
    NgxPaginationModule, NullableNumberPipe,
  ],
  templateUrl: './competitors-table-v2.component.html',
  styleUrl: './competitors-table-v2.component.css',
})
export class CompetitorsTableV2Component implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() gsubind: string[] | null = null;
  @Input() mode: CompetitorMode = 'public';
  @Input() printMode = false;   // PDF report: show all rows, hide controls

  private destroy$ = new Subject<void>();

  snapshot: IInnovationMetricsSnapshot | null = null;
  entityid: string | undefined;

  competitors_list: CompetitorRow[] = [];
  filteredCompanyList: CompetitorRow[] = [];
  showResults = false;
  showDataMissing = false;
  loadingFailed = false;
  paginator_1_page = 1;
  showOnlyFavorites = false;
  searchQuery = '';
  countriesControl = new FormControl<string[]>([]);
  expandedRegions = new Set<string>();
  countryFilterOptions = REGION_CODES;
  selectedCompanies = new Set<string>();

  // Universe selector (custom mode only)
  universeSelection: IUniverseSelection = { include_public: true, include_private: true };
  isUniverseSelectorOpen = false;

  sortColumn = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Optional extra columns — selection shared across tabs/sessions via localStorage
  allExtraColumns = EXTRA_COLUMNS;
  // Always-on score columns, listed (disabled) in the picker for context
  baseColumnLabels = [
    'Size (10y)', 'Growth (1y)', 'Quality (0-100, 1y)',
    'Innovation Score', 'Score Change (3m)', 'Rank Change (%, 3m)',
  ];
  visibleColumnKeys = new Set<string>(this.readVisibleColumns());

  selectedCategory = 'all_competitors';
  categoryTabs = [
    {
      key: 'all_competitors', label: 'All Competitors',
      tooltip: 'All companies in GICS, no filter, innovation score ranking.',
      description: 'All companies in the selected peer group, ranked by innovation score.'
    },
    {
      key: 'innovation_leaders', label: 'Innovation Leaders',
      tooltip: 'Innovation Leaders are companies that rank highest based on their innovation score.',
      description: 'Innovation Leaders are the 50 companies with the strongest innovation activity balanced across size, growth and quality.'
    },
    {
      key: 'growth_leaders', label: 'Growth Leaders',
      tooltip: 'Growth Leaders are companies with the highest relative growth, among companies with at least 20 inventions in the last 12 months.',
      description: 'Growth Leaders are the 50 companies with the most significant recent acceleration in innovation.'
    },
    {
      key: 'size_leaders', label: 'Size Leaders',
      tooltip: 'Size Leaders are companies with the highest number of inventions in the last 10 years.',
      description: 'Size Leaders are the 50 companies with the largest innovation portfolios.'
    },
    {
      key: 'quality_leaders', label: 'Quality Leaders',
      tooltip: 'Quality Leaders are companies with the highest innovation quality, among companies with at least 10 inventions in the last 12m.',
      description: 'Quality Leaders are the 50 companies that prioritize quality over quantity.'
    },
    {
      key: 'upcoming', label: 'Up and Coming',
      tooltip: 'Up and Coming are companies with the highest innovation score increase.',
      description: 'Up and Coming are the 50 companies with the highest innovation score increase.'
    },
  ];

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog,
  ) { }

  get title(): string {
    if (this.mode === 'public') return 'Public Competitors';
    if (this.mode === 'private') return 'Private Competitors';
    return 'Custom Peer Group';
  }

  get paginatorId(): string { return `pag_v2_${this.mode}`; }

  get showUniverseSelector(): boolean { return this.mode === 'custom'; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.resetState();

      if (this.mode === 'custom') {
        this.checkCustomPeerGroupState();
      } else {
        this.loadData();
      }
    }
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  private resetState(): void {
    this.snapshot = null;
    this.competitors_list = [];
    this.filteredCompanyList = [];
    this.focusedCompanyRow = null;
    this.hasCustomSelection = false;
    this.showResults = false;
    this.showDataMissing = false;
    this.loadingFailed = false;
    this.selectedCategory = 'all_competitors';
    this.sortColumn = 'innovation_score';
    this.sortDirection = 'desc';
    this.searchQuery = '';
    this.showOnlyFavorites = false;
    this.selectedCompanies.clear();
  }

  // --- Universe selector (custom mode) ---

  hasCustomSelection = false;

  private isRealCustomSelection(u: IUniverseSelection | undefined): boolean {
    if (!u) return false;
    const onlyPublic = u.include_public && !u.include_private && !u.min_mcap && !u.max_mcap;
    const onlyPrivate = u.include_private && !u.include_public && !u.min_mcap && !u.max_mcap;
    return !onlyPublic && !onlyPrivate;
  }

  private checkCustomPeerGroupState(): void {
    this.entityid = this.entityids?.[0];

    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(data => {
      if (this.isRealCustomSelection(data?.universe)) {
        this.universeSelection = data!.universe!;
        this.hasCustomSelection = true;
        this.loadData();
      } else {
        setTimeout(() => this.openUniverseSelector(), 100);
      }
    });
  }

  openUniverseSelector(): void {
    if (this.isUniverseSelectorOpen) return;
    this.isUniverseSelectorOpen = true;

    // Peek at base snapshot (shared with public/private tabs) just to detect the
    // focus company's companytype for the dialog hint. Falls back to 'Public Company'.
    this.ensureBaseSnapshotForCompanyType();

    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(current => {
      let companyType = this.snapshot?.companies.find(c => c.entityid === this.entityid)?.companytype;

      if (companyType === 'Public Company' || companyType === 'Public Investment Firm') {
        companyType = 'Public Company';
      } else if (companyType === 'Private Company' || companyType === 'Private Investment Firm') {
        companyType = 'Private Company';
      } else {
        companyType = 'Public Company';
      }

      const dialogRef = this.dialog.open(UniverseSelectorComponent, {
        data: {
          min_mcap: current?.universe?.min_mcap,
          max_mcap: current?.universe?.max_mcap,
          include_public: current?.universe?.include_public,
          include_private: current?.universe?.include_private,
          search_type: 'company',
          showCloseButton: true,
          company_type: companyType,
        },
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe(result => {
        this.isUniverseSelectorOpen = false;

        if (!result) {
          this.navigateBasedOnSelection();
          return;
        }

        const newSelection: IUniverseSelection = {
          include_private: result.isPrivate,
          include_public: result.isPublic,
          min_mcap: result.min,
          max_mcap: result.max,
        };

        const onlyPublic = result.isPublic && !result.isPrivate && !result.min && !result.max;
        const onlyPrivate = result.isPrivate && !result.isPublic && !result.min && !result.max;

        if (onlyPublic) {
          this.showRedirectDialog('public', newSelection);
          return;
        }
        if (onlyPrivate) {
          this.showRedirectDialog('private', newSelection);
          return;
        }

        // Custom peer group — stay here
        this.store.dispatch(setUniverseSelection({ selection: newSelection, tabId: this.tabId }));
        this.universeSelection = newSelection;
        this.hasCustomSelection = true;
        this.loadData();
      });
    });
  }

  private showRedirectDialog(type: 'public' | 'private', selection: IUniverseSelection): void {
    const label = type === 'public' ? 'Public' : 'Private';
    const sideTab = type === 'public' ? 'company_public_competitors' : 'company_private_competitors';

    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: 'Peer Group Selection',
        message: `Looks like you have selected ${label.toLowerCase()} competitors. Redirecting to ${label} Competitors tab.`,
        confirmText: 'OK',
        cancelText: 'Cancel',
        confirmColor: 'primary',
      },
    });

    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.store.dispatch(setUniverseSelection({ selection, tabId: this.tabId }));
        this.store.dispatch(changeActiveSideTab({ sideTab }));
      } else {
        setTimeout(() => this.openUniverseSelector(), 100);
      }
    });
  }

  private navigateBasedOnSelection(): void {
    this.store.select(selectUniverseSelectionbyTabId(this.tabId)).pipe(take(1)).subscribe(data => {
      const u = data?.universe;
      if (!u) {
        this.store.dispatch(changeActiveSideTab({ sideTab: 'company_public_competitors' }));
        return;
      }
      const onlyPub = u.include_public && !u.include_private && !u.min_mcap && !u.max_mcap;
      const onlyPri = u.include_private && !u.include_public && !u.min_mcap && !u.max_mcap;
      if (onlyPub) this.store.dispatch(changeActiveSideTab({ sideTab: 'company_public_competitors' }));
      else if (onlyPri) this.store.dispatch(changeActiveSideTab({ sideTab: 'company_private_competitors' }));
      else this.store.dispatch(changeActiveSideTab({ sideTab: 'competitive_benchmarking_peergroup' }));
    });
  }

  private loadData(): void {
    this.entityid = this.entityids?.[0];
    if (!this.entityid) { this.showDataMissing = true; return; }

    // Clear stale rows so the spinner shows while the new selection loads.
    this.snapshot = null;
    this.competitors_list = [];
    this.filteredCompanyList = [];
    this.focusedCompanyRow = null;
    this.showDataMissing = false;
    this.loadingFailed = false;
    this.showResults = true;
    const currentTabId = this.tabId;

    // Custom mode → backend filters; each filter combo gets its own cache key.
    // Public/private → shared base snapshot, client filters by companytype.
    const isCustom = this.mode === 'custom';
    const sidenavKey = isCustom
      ? `innovation_snapshot_custom:${this.universeSelectionHash()}`
      : 'innovation_snapshot';
    const filters = isCustom ? this.universeSelection : undefined;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, sidenavKey)).pipe(
      take(1),
      switchMap(cached => {
        if (cached) {
          if (this.tabId === currentTabId) this.applySnapshot(cached);
          return of(null);
        }
        return this.apiService.getInnovationSnapshot(this.entityid!, currentTabId, filters).pipe(
          takeUntil(this.destroy$),
          tap(res => {
            const data = res.body?.payload;
            if (!res.ok || !data?.companies?.length) {
              if (this.tabId === currentTabId) this.showDataMissing = true;
              return;
            }
            const targetTabId = data.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey, data }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) this.applySnapshot(data);
          }),
        );
      }),
    ).subscribe({ error: () => { if (this.tabId === currentTabId) this.loadingFailed = true; } });
  }

  private universeSelectionHash(): string {
    const u = this.universeSelection;
    return `${u.include_public ? 1 : 0}_${u.include_private ? 1 : 0}_${u.min_mcap ?? ''}_${u.max_mcap ?? ''}`;
  }

  // Lightweight prefetch of the unfiltered peer-group snapshot, shared with
  // Public/Private tabs. Used only so the universe-selector dialog can read the
  // focus company's companytype. Snapshot is NOT applied to the table view.
  private ensureBaseSnapshotForCompanyType(): void {
    if (!this.entityid) this.entityid = this.entityids?.[0];
    if (!this.entityid) return;
    const currentTabId = this.tabId;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, 'innovation_snapshot')).pipe(
      take(1),
      switchMap(cached => {
        if (cached) {
          if (this.tabId === currentTabId && !this.snapshot) this.snapshot = cached;
          return of(null);
        }
        return this.apiService.getInnovationSnapshot(this.entityid!, currentTabId).pipe(
          takeUntil(this.destroy$),
          tap(res => {
            const data = res.body?.payload;
            if (!res.ok || !data?.companies?.length) return;
            const targetTabId = data.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: 'innovation_snapshot', data }));
            if (this.tabId === currentTabId && targetTabId === currentTabId && !this.snapshot) this.snapshot = data;
          }),
        );
      }),
    ).subscribe();
  }

  private applySnapshot(data: IInnovationMetricsSnapshot): void {
    this.snapshot = data;
    this.buildCompetitorsList();
  }

  focusedCompanyRow: CompetitorRow | null = null;

  private buildCompetitorsList(): void {
    if (!this.snapshot) return;
    let companies = this.snapshot.companies;

    // Filter by company type, but always keep the focused company
    const isFocused = (c: IInnovationMetricsCompany) => c.entityid === this.entityid;

    if (this.mode === 'public') {
      companies = companies.filter(c => isFocused(c) || c.companytype?.toLowerCase().includes('public'));
    } else if (this.mode === 'private') {
      companies = companies.filter(c => isFocused(c) || !c.companytype?.toLowerCase().includes('public'));
    }
    // custom mode → backend already filtered; use payload as-is.

    // Backend sanitize_floats turns pandas NaN into " " (space); coerce those
    // (and any other non-finite) to null so template null-guards work.
    const num = (v: any) => (typeof v === 'number' && isFinite(v) ? v : null);

    // Sort by innovation_score desc and assign rank within this filtered set
    const sorted = [...companies].sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
    this.competitors_list = sorted.map((c, i) => ({
      ...c,
      score_change: num(c.score_change),
      rank_climbed: num(c.rank_climbed),
      pct_rank_climbed: num(c.pct_rank_climbed),
      rank: i + 1,
      favorite: false,
      score_bracket: c.score_bracket ?? 3,
      rank_bracket: c.rank_bracket ?? 3,
    }));

    this.focusedCompanyRow = this.competitors_list.find(c => c.entityid === this.entityid) ?? null;
    this.showDataMissing = this.competitors_list.length === 0;
    this.applyFilters();
  }

  // --- Filtering & Sorting ---

  applyFilters(): void {
    let list = this.competitors_list;
    if (this.showOnlyFavorites) list = list.filter(c => c.favorite || this.selectedCompanies.has(c.entityid));
    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(c => (c.entity_name || '').toLowerCase().includes(q));
    }
    const countries = this.countriesControl.value ?? [];
    if (countries.length > 0) list = list.filter(c => countries.includes(c.country || ''));

    list = this.applyCategoryFilter(list);
    this.filteredCompanyList = this.applySorting(list);
    this.paginator_1_page = 1;
  }

  private applyCategoryFilter(list: CompetitorRow[]): CompetitorRow[] {
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        return list.sort((a, b) => b.innovation_score - a.innovation_score).slice(0, 50);
      case 'growth_leaders':
        return list.filter(c => (c.invention_count_12m ?? 0) >= 20)
          .sort((a, b) => (b.relative_growth_12m ?? 0) - (a.relative_growth_12m ?? 0)).slice(0, 50);
      case 'size_leaders':
        return list.sort((a, b) => (b.invention_count_120m ?? 0) - (a.invention_count_120m ?? 0)).slice(0, 50);
      case 'quality_leaders':
        return list.filter(c => (c.invention_count_12m ?? 0) >= 10)
          .sort((a, b) => (b.quality_score_12m ?? 0) - (a.quality_score_12m ?? 0)).slice(0, 50);
      case 'upcoming':
        return list.filter(c => (c.score_change ?? 0) >= 0)
          .sort((a, b) => (b.score_change ?? 0) - (a.score_change ?? 0)).slice(0, 50);
      default:
        return list;
    }
  }

  private applySorting(list: CompetitorRow[]): CompetitorRow[] {
    if (this.selectedCategory !== 'all_competitors') return list;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let aVal = 0, bVal = 0;
      switch (this.sortColumn) {
        case 'rank': aVal = a.rank; bVal = b.rank; break;
        case 'size': aVal = a.invention_count_120m ?? 0; bVal = b.invention_count_120m ?? 0; break;
        case 'growth': aVal = a.relative_growth_12m ?? 0; bVal = b.relative_growth_12m ?? 0; break;
        case 'quality': aVal = a.quality_score_12m ?? 0; bVal = b.quality_score_12m ?? 0; break;
        case 'innovation_score': aVal = a.innovation_score ?? 0; bVal = b.innovation_score ?? 0; break;
        case 'score_change': aVal = a.score_change ?? 0; bVal = b.score_change ?? 0; break;
        case 'rank_change': aVal = a.pct_rank_climbed ?? 0; bVal = b.pct_rank_climbed ?? 0; break;
        default: {
          const col = EXTRA_COLUMNS.find(c => c.key === this.sortColumn);
          if (col) { aVal = col.value(a) ?? 0; bVal = col.value(b) ?? 0; }
        }
      }
      return (aVal - bVal) * dir;
    });
  }

  sort(column: string): void {
    if (this.selectedCategory !== 'all_competitors') return;
    this.sortDirection = this.sortColumn === column
      ? (this.sortDirection === 'asc' ? 'desc' : 'asc')
      : 'desc';
    this.sortColumn = column;
    this.applyFilters();
  }

  getSortIcon(col: string): string {
    if (this.sortColumn !== col) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  // --- Extra columns ---

  private readVisibleColumns(): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem(EXTRA_COLUMNS_STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((k: string) => EXTRA_COLUMNS.some(c => c.key === k)) : [];
    } catch {
      return [];
    }
  }

  get visibleExtraColumns(): ExtraColumn[] {
    if (this.printMode) return [];
    return EXTRA_COLUMNS.filter(c => this.visibleColumnKeys.has(c.key));
  }

  isColumnVisible(key: string): boolean { return this.visibleColumnKeys.has(key); }

  toggleColumn(key: string): void {
    this.visibleColumnKeys.has(key) ? this.visibleColumnKeys.delete(key) : this.visibleColumnKeys.add(key);
    localStorage.setItem(EXTRA_COLUMNS_STORAGE_KEY, JSON.stringify([...this.visibleColumnKeys]));
    // A hidden column must not stay the active sort key.
    if (!this.visibleColumnKeys.has(key) && this.sortColumn === key) {
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
      this.applyFilters();
    }
  }

  selectCategory(key: string): void {
    this.selectedCategory = key;
    this.applyFilters();
  }

  getCurrentCategoryDescription(): string {
    return this.categoryTabs.find(t => t.key === this.selectedCategory)?.description || '';
  }
  getCurrentCategoryTooltip(): string {
    return this.categoryTabs.find(t => t.key === this.selectedCategory)?.tooltip || '';
  }
  getCurrentCategoryLabel(): string {
    return this.categoryTabs.find(t => t.key === this.selectedCategory)?.label || '';
  }
  hasCategoryResults(): boolean { return this.filteredCompanyList.length > 0; }

  getCategoryDescription(): string {
    const c = this.focusedCompanyRow;
    if (!c) return '';
    const name = c.entity_name || 'Unknown Company';
    const peerName =this.snapshot?.peer_group_name || c.gsubind_extended_name || c.subindustryname || '';
    const cat = this.categoryTabs.find(t => t.key === this.selectedCategory);
    if (!cat) return '';
    const inList = this.filteredCompanyList.some(r => r.entityid === c.entityid);
    const rankInList = this.filteredCompanyList.findIndex(r => r.entityid === c.entityid) + 1;
    const count = this.filteredCompanyList.length;

    if (this.selectedCategory === 'all_competitors') {
      return `<b>${name}</b> ranks <b>${c.rank}${this.getOrdinalSuffix(c.rank)}</b> in innovation strength among <b>${count}</b> competitors in the peer group <b>${peerName}</b>.`;
    }
    if (inList) {
      return `<b>${name}</b> ranks <b>${rankInList}${this.getOrdinalSuffix(rankInList)}</b> among the <b>${count}</b> <b>${cat.label}</b> in the peer group <b>${peerName}</b>.`;
    }
    return `<b>${name}</b> is not among the <b>${cat.label}</b> in the peer group <b>${peerName}</b>.`;
  }

  filterFavorites(): void { this.showOnlyFavorites = !this.showOnlyFavorites; this.applyFilters(); }

  // --- Selection ---

  toggleCompanySelection(company: CompetitorRow): void {
    if (this.selectedCompanies.has(company.entityid)) {
      this.selectedCompanies.delete(company.entityid);
    } else {
      this.selectedCompanies.add(company.entityid);
    }
  }
  isCompanySelected(entityid: string): boolean { return this.selectedCompanies.has(entityid); }
  getSelectedEligibleCompanies() {
    const selected = this.competitors_list.filter(c => this.selectedCompanies.has(c.entityid));
    const eligible = selected.filter(c => this.isEligibleForWatchlist(c));
    if (eligible != null) {
      return eligible.length;
    }
    else {
      return 0;
    }

  }


  isPublicCompany(company: CompetitorRow): boolean {
    return company.companytype === 'Public Company' || company.companytype === 'Public Investment Firm';
  }

  hasGICS(company: CompetitorRow): boolean {
    const comp = company as any;
    return !!(comp.gsubind || comp.gsubind_extended || comp.entity_gics || comp.subindustryname);
  }

  isEligibleForWatchlist(company: CompetitorRow): boolean {
    return this.isPublicCompany(company) && this.hasGICS(company);
  }

  openAddToWatchlist(): void {
    const selected = this.competitors_list.filter(c => this.selectedCompanies.has(c.entityid));
    const eligible = selected.filter(c => this.isEligibleForWatchlist(c));
    const privateCompanies = selected.filter(c => !this.isEligibleForWatchlist(c));
    const companies = eligible.map(c => ({
      entityid: c.entityid,
      entity_name: c.entity_name ?? '',
      entity_gics: c.gsubind_extended || c.gsubind || undefined,
      isin: c.isin ?? undefined,
      companytype: c.companytype,
      gsubind: c.gsubind,
      gsubind_extended: c.gsubind_extended ?? undefined,
    }));


    if (privateCompanies.length > 0) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '450px',
        data: {
          title: 'Add to Watchlist',
          message: `Out of ${selected.length} selected companies, ${eligible.length} public companies with valid GICS will be added to the watchlist. ${privateCompanies.length} private companies cannot be added.`,
          confirmText: 'Continue',
          cancelText: 'Cancel',
          confirmColor: 'primary',
        },
      });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
        if (confirmed && eligible.length > 0) {
          this.dialog.open(AddToWatchlistPopupComponent, {
            width: '600px',
            data: { companies: companies, tabId: this.tabId },
          });
        }
      });
    } else if (eligible.length > 0) {
      this.dialog.open(AddToWatchlistPopupComponent, {
        width: '600px',
        data: { companies: companies, tabId: this.tabId },
      });
    }
  }
  // --- Country filter ---

  toggleExpand(region: string): void {
    this.expandedRegions.has(region) ? this.expandedRegions.delete(region) : this.expandedRegions.add(region);
  }
  isExpanded(region: string): boolean { return this.expandedRegions.has(region); }

  toggleRegionSelection(region: any): void {
    const codes = region.countries.map((c: any) => c.code);
    const allSelected = codes.every((c: string) => (this.countriesControl.value ?? []).includes(c));
    this.countriesControl.setValue(allSelected
      ? (this.countriesControl.value ?? []).filter((v: string) => !codes.includes(v))
      : [...new Set([...(this.countriesControl.value ?? []), ...codes])]
    );
    this.applyFilters();
  }

  toggleCountrySelection(code: string): void {
    const cur = this.countriesControl.value ?? [];
    this.countriesControl.setValue(cur.includes(code) ? cur.filter((v: string) => v !== code) : [...cur, code]);
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const codes = region.countries.map((c: any) => c.code);
    return codes.every((c: string) => (this.countriesControl.value ?? []).includes(c));
  }

  // --- Display helpers ---

  getBracketColor = getBracketColor;
  getBracketArrow = getBracketArrow;
  getBracketTooltip = getBracketTooltip;
  getBarColor = getBarColor;
  getSignalColor = getSignalColor;
  getSignalBgColor = getSignalBgColor;

  getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  newSearch(company: CompetitorRow): void {
    if (!company.gsubind_extended) {
      company.gsubind_extended = Array.isArray(company.gsubind) ? company.gsubind[0] : company.gsubind;
    }
    const searchParams = { companies: [company] };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'company' }));
  }

  // --- Excel export ---

  downloadExcel(): void {
    const rows = this.competitors_list.map(c => ({
      Rank: c.rank,
      Company: c.entity_name,
      Country: c.country,
      Type: c.companytype,
      'Size (10y)': c.invention_count_120m,
      'Growth (1y %)': c.relative_growth_12m ? Math.round(c.relative_growth_12m * 10) / 10 : null,
      'Quality (12m)': c.quality_score_12m ? Math.round(c.quality_score_12m * 10) / 10 : null,
      'Innovation Score': c.innovation_score ? Math.round(c.innovation_score * 10) / 10 : null,
      'Score Change (3m)': c.score_change ? Math.round(c.score_change * 10) / 10 : null,
      'Rank Change (% 3m)': c.pct_rank_climbed ? Math.round(c.pct_rank_climbed * 10) / 10 : null,
      // All extra columns are exported, regardless of what is shown in the table.
      ...Object.fromEntries(EXTRA_COLUMNS.flatMap(col => {
        const name = `${col.label} (${col.sublabel})`;
        const value = col.value(c);
        return [
          [name, value == null ? null : Math.round(value * 100) / 100],
          [`${name} — comparison`, col.secondary(c)],
        ];
      })),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.mode);
    const name = (this.tab_name || 'competitors').substring(0, 30);
    XLSX.writeFile(wb, `${name}_${this.mode}_competitors.xlsx`);
  }
}
